import { useState, type ReactElement } from "react";
import { useMutation } from "@tanstack/react-query";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import { Stack, TextField } from "@mui/material";
import { useTranslation, type TranslationKey } from "@privacy/translations";
import {
  AUTOCOMPLETE_LOADING_OPTION,
  AutocompleteOption,
  CreateNewAutocompleteOption,
  createFilterAutocompleteOption,
  withCreateNewOption,
  type AutocompleteOptionType,
  type AutocompleteOptionWithMaybeIsNew,
} from "../../shared/Autocomplete";
import type { IconProps } from "../../shared/icons";
import { DynamicShared } from "../../dynamicShared";
import { EmptyPlaceholderText } from "../inlineForm/EmptyPlaceholderText";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import { inlineInputStateStyles, useInlineInputState } from "../inlineForm/inputs/InlineInputState";

type InlineEntityMultiSelectProps = {
  id: string;
  value: AutocompleteOptionType[];
  options: AutocompleteOptionType[] | undefined;
  onSave: (selected: AutocompleteOptionType[]) => Promise<unknown>;
  renderIcon: (props: IconProps) => ReactElement<IconProps>;
  noOptionsTextKey: TranslationKey;
  isReadOnly?: boolean;
  onCreateNew?: (inputValue: string) => void;
  saveAfterAdditions?: number;
  onHover?: () => void;
};

export const InlineEntityMultiSelect = ({
  id,
  value,
  options,
  onSave,
  renderIcon,
  noOptionsTextKey,
  isReadOnly = false,
  onCreateNew,
  saveAfterAdditions,
  onHover,
}: InlineEntityMultiSelectProps) => {
  const { t } = useTranslation();
  const { inputState, setIsEditing } = useInlineInputState(isReadOnly);
  const [draft, setDraft] = useState<AutocompleteOptionType[] | null>(null);
  const saveMutation = useMutation({ mutationFn: onSave, onSettled: () => setDraft(null) });

  const selected: AutocompleteOptionWithMaybeIsNew[] = draft ?? value;
  const isEmpty = selected.length === 0;
  const isSaved = (option: AutocompleteOptionType) => value.some(saved => saved.value === option.value);
  const listOptions: AutocompleteOptionWithMaybeIsNew[] = options ?? [AUTOCOMPLETE_LOADING_OPTION];

  const save = (selection: AutocompleteOptionType[]) => {
    setIsEditing(false);
    if (selection.length !== value.length || !selection.every(isSaved)) saveMutation.mutate(selection);
    else setDraft(null);
  };

  return (
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Autocomplete<AutocompleteOptionWithMaybeIsNew, true>
        id={id}
        multiple
        openOnFocus
        disablePortal
        disableCloseOnSelect
        clearOnBlur
        clearIcon={null}
        disabled={isReadOnly || saveMutation.isPending}
        noOptionsText={t(noOptionsTextKey)}
        options={listOptions}
        value={selected}
        onMouseOver={onHover}
        onFocus={() => {
          onHover?.();
          setIsEditing(true);
          setDraft(selected);
        }}
        onBlur={() => {
          if (draft && !saveMutation.isPending) save(draft);
          else setIsEditing(false);
        }}
        onChange={(_, selection) => {
          const created = selection.find(option => option.isNew);
          if (created) {
            onCreateNew?.(created.value);
            return;
          }
          setDraft(selection);
          if (selection.filter(option => !isSaved(option)).length === saveAfterAdditions) save(selection);
        }}
        filterOptions={(sourceOptions, params) => {
          const filtered = createFilterAutocompleteOption(sourceOptions, params);
          return onCreateNew && options ? withCreateNewOption(filtered, params.inputValue, t) : filtered;
        }}
        isOptionEqualToValue={(option, selectedOption) =>
          option.value === AUTOCOMPLETE_LOADING_OPTION.value || option.value === selectedOption.value
        }
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return option.isNew ? (
            <CreateNewAutocompleteOption key={option.value} {...optionProps} option={option} />
          ) : (
            <AutocompleteOption key={option.value} {...optionProps} option={option} dense />
          );
        }}
        renderValue={(selectedOptions, getItemProps) =>
          selectedOptions.map((option, index) => {
            const { key, onDelete, tabIndex } = getItemProps({ index });
            return (
              <DynamicShared.Chip
                key={key}
                label={option.label}
                tabIndex={tabIndex}
                onDelete={onDelete}
                inputState={inputState}
                variant="entity"
                renderIcon={renderIcon}
              />
            );
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <>
                    {inputState !== "editing" && isEmpty && <EmptyPlaceholderText />}
                    {params.slotProps.input.startAdornment}
                  </>
                ),
                sx: {
                  ...inlineInputStateStyles[inputState],
                  [`&.${autocompleteClasses.inputRoot}`]: { gap: isEmpty ? 0 : 1 },
                },
              },
            }}
          />
        )}
      />
      {saveMutation.isError && <ErrorMessage />}
    </Stack>
  );
};
